import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type { Comissao, FiltroComissao, RegraComissao, TipoComissaoConfiguravel, CategoriaServico } from '@pgcustom/shared-types';

export interface FiltroRegraComissao {
  pessoaId?: string;
  tipoComissao?: TipoComissaoConfiguravel;
}

/**
 * Adapter do módulo Comissões no frontend — único ponto que conhece o
 * formato das rotas `/api/v1/comissoes/*`
 * (`apps/api/src/modules/comissoes/comissoes.routes.ts`). `listar`
 * devolve `Resultado<T>` (alimenta estados de UI diretamente); `pagar`
 * propaga `ApiError` — quem decide como exibir a falha é a tela que
 * chama. Sem `criar`: comissão só nasce de listener de evento
 * (`pedido.pago`/`ops.concluida`), nunca via HTTP.
 */
export class ComissoesApi {
  constructor(private readonly api: ApiClient) {}

  async listar(filtro: FiltroComissao = {}): Promise<Resultado<Comissao[]>> {
    try {
      const resposta = await this.api.get<{ comissoes: Comissao[] }>('/comissoes', { ...filtro });
      if (resposta.comissoes.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.comissoes };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar comissões' };
    }
  }

  async pagar(id: string, contaId: string): Promise<Comissao> {
    const resposta = await this.api.post<{ comissao: Comissao }>(`/comissoes/${id}/pagar`, { contaId });
    return resposta.comissao;
  }

  /** Self-service ("minhas comissões", fase de finalização) — `GET /comissoes/me`, resolvido pelo backend a partir da própria sessão. */
  async minhas(tipo?: Comissao['tipo']): Promise<Resultado<Comissao[]>> {
    try {
      const resposta = await this.api.get<{ comissoes: Comissao[] }>('/comissoes/me', tipo ? { tipo } : {});
      if (resposta.comissoes.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.comissoes };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar minhas comissões' };
    }
  }

  /** `GET /comissoes/regras` — recurso `comissoes_regras` (migration 024): configuração de comissão por pessoa. */
  async listarRegras(filtro: FiltroRegraComissao = {}): Promise<Resultado<RegraComissao[]>> {
    try {
      const resposta = await this.api.get<{ regras: RegraComissao[] }>('/comissoes/regras', { ...filtro });
      if (resposta.regras.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.regras };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar regras de comissão' };
    }
  }

  /** `GET /comissoes/categorias` — recurso `categorias_servico` (migration 026): catálogo de categorias de serviço técnico. */
  async listarCategorias(): Promise<Resultado<CategoriaServico[]>> {
    try {
      const resposta = await this.api.get<{ categorias: CategoriaServico[] }>('/comissoes/categorias');
      if (resposta.categorias.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.categorias };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar categorias de serviço' };
    }
  }
}
